import { Event, Application } from '../types';
import { supabase } from '../lib/supabaseClient';
import { featuredEventProducts } from '../stripe-config';

// Helper types for enriched applications
export type ApplicantSummary = {
  id: string;
  username?: string | null;
  fullName?: string | null;
  bio?: string | null;
  isVerified?: boolean | null;
  rating?: number | null;
  ratingCount?: number | null;
  avatarUrl?: string | null;
};
export type ApplicationEnriched = Application & { applicant?: ApplicantSummary };

// NOTE: createCheckoutSession must exist in your project (import it where you keep it)

// ---------------------------
// Seed/Init (no-op)
// ---------------------------
export const initializeSampleData = () => {
  console.log('Sample data initialization skipped - using Supabase database');
};

// ---------------------------
/** Helpers / types */
// ---------------------------
type ContactMethod = 'in_app' | 'email' | 'phone' | 'whatsapp' | 'instagram';

function mapEventRow(event: any): Event {
  return {
    id: event.id,
    title: event.title,
    description: event.description,
    location: event.location,
    date: event.date,
    category: event.category,
    posterId: event.poster_id,
    latitude: event.latitude,
    longitude: event.longitude,
    maxApplicants: event.max_applicants,
    currentApplicants: event.current_applicants,
    preferredDemographics: {
      gender: event.preferred_gender,
      minAge: event.min_age,
      maxAge: event.max_age,
      interests: event.preferred_interests,
    },
    isFeatured: event.is_featured,
    featuredUntil: event.featured_until,
    status: event.status,
    visibility: event.visibility,
    createdAt: event.created_at,
    imageUrl: event.image_url,
    unsplashPhotoUrl: event.unsplash_photo_url,
    imageGenerationStatus: event.image_generation_status,
    isBotCreated: event.is_bot_created,
    botGenerationId: event.bot_generation_id,
    autoAcceptEnabled: event.auto_accept_enabled,
    systemHost: event.system_host,
    poster: event.poster ? {
      username: event.poster.username ? String(event.poster.username).replace(/0+$/, '') : event.poster.username,
      rating: event.poster.rating,
      ratingCount: event.poster.rating_count,
      subscriptionTier: event.poster.subscription_tier,
      avatarUrl: event.poster.avatar_url,
    } : undefined,
    acceptedCount: event.acceptedCount || 0,
  };
}

function mapApplicationRow(app: any): Application {
  return {
    id: app.id,
    eventId: app.event_id,
    applicantId: app.applicant_id,
    message: app.message,
    status: app.status,
    createdAt: app.created_at,
  };
}

// ---------------------------
// Events (queries & mutations)
// ---------------------------
export const getAllEvents = async (
  _userId?: string,
  options?: {
    limit?: number;
    offset?: number;
    vybeType?: 'community' | 'hosted' | 'all';
    category?: string;
    dateFrom?: string;
    dateTo?: string;
  }
): Promise<Event[]> => {
  try {
    // Get today's date at midnight for filtering past events
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = today.toISOString().split('T')[0]; // YYYY-MM-DD

    let query = supabase
      .from('events')
      .select(`
        *,
        poster:profiles!events_poster_id_fkey(username, rating, rating_count, subscription_tier, avatar_url)
      `)
      .eq('status', 'published')
      .gte('date', todayString) // Only fetch events from today onwards
      .order('date', { ascending: true });

    // Apply vybe type filter
    if (options?.vybeType === 'community') {
      query = query.or('is_bot_created.eq.true,system_host.eq.true');
    } else if (options?.vybeType === 'hosted') {
      query = query.eq('is_bot_created', false).eq('system_host', false);
    }
    // If 'all' or undefined, don't filter by type

    // Apply category filter (handle both emoji and non-emoji versions)
    if (options?.category) {
      // Extract the text part without emoji (e.g., "🎮 Gaming & Board Games" -> "Gaming & Board Games")
      const categoryText = options.category.replace(/[\u{1F300}-\u{1F9FF}][\u{FE00}-\u{FE0F}]?/gu, '').trim();
      // Match either the full string with emoji or just the text part
      query = query.or(`category.eq.${options.category},category.eq.${categoryText}`);
    }

    // Apply date range filters
    if (options?.dateFrom) {
      query = query.gte('date', options.dateFrom);
    }
    if (options?.dateTo) {
      query = query.lte('date', options.dateTo);
    }

    // Apply pagination if provided
    if (options?.limit) {
      query = query.limit(options.limit);
    }
    if (options?.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 20) - 1);
    }

    const { data, error } = await query;

    if (error || !data) return [];

    // Fetch accepted counts for all events
    const eventIds = data.map(e => e.id);
    const { data: acceptedCounts } = await supabase
      .from('applications')
      .select('vybe_id')
      .in('vybe_id', eventIds)
      .eq('status', 'accepted');

    // Count applications by event
    const countsMap = new Map<string, number>();
    if (acceptedCounts) {
      acceptedCounts.forEach(app => {
        countsMap.set(app.vybe_id, (countsMap.get(app.vybe_id) || 0) + 1);
      });
    }

    // Add counts to events
    const eventsWithCounts = data.map(event => ({
      ...event,
      acceptedCount: countsMap.get(event.id) || 0
    }));

    return eventsWithCounts.map(mapEventRow);
  } catch (e) {
    console.error('Error in getAllEvents:', e);
    return [];
  }
};

// Get total count of published events for pagination
export const getEventsCount = async (
  vybeType?: 'community' | 'hosted' | 'all',
  category?: string,
  dateFrom?: string,
  dateTo?: string
): Promise<number> => {
  try {
    // Get today's date at midnight for filtering past events
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = today.toISOString().split('T')[0]; // YYYY-MM-DD

    let query = supabase
      .from('events')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'published')
      .gte('date', todayString); // Only count events from today onwards

    // Apply vybe type filter
    if (vybeType === 'community') {
      query = query.or('is_bot_created.eq.true,system_host.eq.true');
    } else if (vybeType === 'hosted') {
      query = query.eq('is_bot_created', false).eq('system_host', false);
    }

    // Apply category filter (handle both emoji and non-emoji versions)
    if (category) {
      // Extract the text part without emoji (e.g., "🎮 Gaming & Board Games" -> "Gaming & Board Games")
      const categoryText = category.replace(/[\u{1F300}-\u{1F9FF}][\u{FE00}-\u{FE0F}]?/gu, '').trim();
      // Match either the full string with emoji or just the text part
      query = query.or(`category.eq.${category},category.eq.${categoryText}`);
    }

    // Apply date range filters
    if (dateFrom) {
      query = query.gte('date', dateFrom);
    }
    if (dateTo) {
      query = query.lte('date', dateTo);
    }

    const { count, error } = await query;

    if (error) {
      console.error('Error getting events count:', error);
      return 0;
    }
    return count || 0;
  } catch (e) {
    console.error('Error in getEventsCount:', e);
    return 0;
  }
};

export const getFeaturedEvents = async (_userId?: string): Promise<Event[]> => {
  try {
    // Get today's date at midnight for filtering past events
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = today.toISOString().split('T')[0]; // YYYY-MM-DD

    const { data, error } = await supabase
      .from('events')
      .select(`
        *,
        poster:profiles!events_poster_id_fkey(username, rating, rating_count, subscription_tier, avatar_url)
      `)
      .eq('status', 'published')
      .eq('is_featured', true)
      .gte('date', todayString) // Only fetch featured events from today onwards
      .order('date', { ascending: true });

    if (error || !data) return [];

    // Fetch accepted counts for all featured events
    const eventIds = data.map(e => e.id);
    const { data: acceptedCounts } = await supabase
      .from('applications')
      .select('vybe_id')
      .in('vybe_id', eventIds)
      .eq('status', 'accepted');

    // Count applications by event
    const countsMap = new Map<string, number>();
    if (acceptedCounts) {
      acceptedCounts.forEach(app => {
        countsMap.set(app.vybe_id, (countsMap.get(app.vybe_id) || 0) + 1);
      });
    }

    // Add counts to events
    const eventsWithCounts = data.map(event => ({
      ...event,
      acceptedCount: countsMap.get(event.id) || 0
    }));

    return eventsWithCounts.map(mapEventRow);
  } catch (e) {
    console.error('Error in getFeaturedEvents:', e);
    return [];
  }
};

export const getEventById = async (eventId: string): Promise<Event | undefined> => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('id', eventId)
      .maybeSingle();

    if (error) {
      console.error('Supabase error in getEventById:', error);
      return undefined;
    }

    if (!data) {
      console.warn('No event found with ID:', eventId);
      return undefined;
    }

    return mapEventRow(data);
  } catch (e) {
    console.error('Error in getEventById:', e);
    return undefined;
  }
};

export const getEventsByUser = async (userId: string): Promise<Event[]> => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('poster_id', userId)
      .eq('is_bot_created', false)
      .not('status', 'eq', 'archived')
      .order('date', { ascending: true });

    if (error || !data) return [];
    return data.map(mapEventRow);
  } catch (e) {
    console.error('Error in getEventsByUser:', e);
    return [];
  }
};

export const getArchivedEventsByUser = async (userId: string): Promise<Event[]> => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('poster_id', userId)
      .eq('status', 'archived')
      .order('date', { ascending: true });

    if (error || !data) return [];
    return data.map(mapEventRow);
  } catch (e) {
    console.error('Error in getArchivedEventsByUser:', e);
    return [];
  }
};

export const getUpcomingVybesByUser = async (userId: string): Promise<Event[]> => {
  try {
    const { data: acceptedApps, error: appsError } = await supabase
      .from('event_applications')
      .select('event_id')
      .eq('applicant_id', userId)
      .eq('status', 'accepted');

    const acceptedEventIds = acceptedApps?.map(app => app.event_id) || [];

    const { data, error } = await supabase
      .from('events')
      .select('*')
      .or(`poster_id.eq.${userId},id.in.(${acceptedEventIds.length > 0 ? acceptedEventIds.join(',') : 'null'})`)
      .not('status', 'eq', 'archived')
      .gte('date', new Date().toISOString())
      .order('date', { ascending: true });

    if (error || !data) return [];
    return data.map(mapEventRow);
  } catch (e) {
    console.error('Error in getUpcomingVybesByUser:', e);
    return [];
  }
};

export const getSharedEvents = async (currentUserId: string, otherUserId: string): Promise<Event[]> => {
  try {
    // Get events where both users were accepted applicants
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .in('id',
        supabase
          .from('event_applications')
          .select('event_id')
          .eq('applicant_id', currentUserId)
          .eq('status', 'accepted')
      );

    if (error || !data) return [];

    // Filter to only include events where the other user was also accepted or was the poster
    const sharedEvents = [];
    for (const event of data) {
      // Check if other user was the poster
      if (event.poster_id === otherUserId) {
        sharedEvents.push(mapEventRow(event));
        continue;
      }

      // Check if other user was an accepted applicant
      const { data: otherUserApp } = await supabase
        .from('event_applications')
        .select('id')
        .eq('event_id', event.id)
        .eq('applicant_id', otherUserId)
        .eq('status', 'accepted')
        .maybeSingle();

      if (otherUserApp) {
        sharedEvents.push(mapEventRow(event));
      }
    }

    return sharedEvents;
  } catch (e) {
    console.error('Error in getSharedEvents:', e);
    return [];
  }
};

export const archiveEvent = async (eventId: string): Promise<boolean> => {
  try {
    const { error } = await supabase.from('events').update({ status: 'archived' }).eq('id', eventId);
    return !error;
  } catch (e) {
    console.error('Error in archiveEvent:', e);
    return false;
  }
};

export const cancelEvent = async (eventId: string): Promise<boolean> => {
  try {
    const { error } = await supabase.from('events').update({ status: 'cancelled' }).eq('id', eventId);
    return !error;
  } catch (e) {
    console.error('Error in cancelEvent:', e);
    return false;
  }
};

export const restoreEvent = async (eventId: string): Promise<boolean> => {
  try {
    const { error } = await supabase.from('events').update({ status: 'published' }).eq('id', eventId);
    return !error;
  } catch (e) {
    console.error('Error in restoreEvent:', e);
    return false;
  }
};

export const deleteEvent = async (eventId: string): Promise<boolean> => {
  try {
    const { error } = await supabase.from('events').delete().eq('id', eventId);
    return !error;
  } catch (e) {
    console.error('Error in deleteEvent:', e);
    return false;
  }
};

export const updateEvent = async (
  eventId: string,
  updatedData: Partial<Event> & { contactMethod?: ContactMethod; contactValue?: string | null; imageUrl?: string | null }
): Promise<Event | null> => {
  try {
    const supabaseEventData: Record<string, any> = {};

    // Track if title or description changed (for image regeneration)
    const titleOrDescriptionChanged =
      updatedData.title !== undefined ||
      updatedData.description !== undefined;

    // Check if admin provided a custom image URL
    const hasCustomImageUrl = updatedData.imageUrl !== undefined && updatedData.imageUrl !== null && updatedData.imageUrl.trim() !== '';

    if (updatedData.title !== undefined) supabaseEventData.title = updatedData.title;
    if (updatedData.description !== undefined) supabaseEventData.description = updatedData.description;
    if (updatedData.location !== undefined) supabaseEventData.location = updatedData.location;
    if (updatedData.date !== undefined) supabaseEventData.date = updatedData.date;
    if (updatedData.category !== undefined) supabaseEventData.category = updatedData.category;
    if (updatedData.latitude !== undefined) supabaseEventData.latitude = updatedData.latitude;
    if (updatedData.longitude !== undefined) supabaseEventData.longitude = updatedData.longitude;
    if (updatedData.maxApplicants !== undefined) supabaseEventData.max_applicants = updatedData.maxApplicants;
    if (updatedData.status !== undefined) supabaseEventData.status = updatedData.status;
    if (updatedData.visibility !== undefined) supabaseEventData.visibility = updatedData.visibility;

    if (updatedData.preferredDemographics) {
      const pd = updatedData.preferredDemographics;
      if (pd.gender !== undefined) supabaseEventData.preferred_gender = pd.gender;
      if (pd.minAge !== undefined) supabaseEventData.min_age = pd.minAge;
      if (pd.maxAge !== undefined) supabaseEventData.max_age = pd.maxAge;
      if (pd.interests !== undefined) supabaseEventData.preferred_interests = pd.interests;
    }

    if (updatedData.contactMethod !== undefined) supabaseEventData.contact_method = updatedData.contactMethod;
    if (updatedData.contactValue !== undefined) supabaseEventData.contact_value = updatedData.contactValue;

    // Handle custom image URL (admin override)
    if (hasCustomImageUrl) {
      supabaseEventData.image_url = updatedData.imageUrl;
      supabaseEventData.image_generation_status = 'completed';
    }
    // If title or description changed and no custom image URL, mark image for regeneration
    else if (titleOrDescriptionChanged) {
      supabaseEventData.image_generation_status = 'pending';
    }

    const { data, error } = await supabase
      .from('events')
      .update(supabaseEventData)
      .eq('id', eventId)
      .select()
      .single();

    if (error || !data) throw new Error(error?.message || 'Failed to update event');

    // Notify accepted applicants if the event was updated
    try {
      // Get all accepted applicants for this event
      const { data: acceptedApps, error: appsError } = await supabase
        .from('event_applications')
        .select('applicant_id, event_id')
        .eq('event_id', eventId)
        .eq('status', 'accepted');

      if (!appsError && acceptedApps && acceptedApps.length > 0) {
        // Create notifications for each accepted applicant
        const notifications = acceptedApps.map(app => ({
          user_id: app.applicant_id,
          type: 'vybe_updated',
          content: `The Vybe "${updatedData.title || data.title}" has been updated by the host`,
          link: `/vybes/${eventId}`,
          read: false,
        }));

        await supabase.from('notifications').insert(notifications);
      }
    } catch (notifError) {
      // Log but don't fail the update if notification fails
      console.error('Error sending update notifications:', notifError);
    }

    // Regenerate image if title or description changed (but only if no custom image URL provided)
    if (titleOrDescriptionChanged && !hasCustomImageUrl) {
      try {
        // Trigger image regeneration via edge function
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        await fetch(`${supabaseUrl}/functions/v1/generate-vybe-image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({
            eventId: eventId,
            title: updatedData.title || data.title,
            description: updatedData.description || data.description,
            category: data.category,
            location: updatedData.location || data.location,
          }),
        });
      } catch (imageError) {
        // Log but don't fail the update if image generation fails
        console.error('Error triggering image regeneration:', imageError);
      }
    }

    return mapEventRow(data);
  } catch (e) {
    console.error('Error in updateEvent:', e);
    return null;
  }
};

export const createEvent = async (
  eventData: Omit<Event, 'id' | 'createdAt'> & {
    contactMethod?: ContactMethod;
    contactValue?: string | null;
    imageUrl?: string | null;
    sourceType?: string;
    systemHost?: boolean;
    isBotCreated?: boolean;
    autoAcceptEnabled?: boolean;
  }
): Promise<Event> => {
  try {
    const supabaseEventData: Record<string, any> = {
      title: eventData.title,
      description: eventData.description,
      location: eventData.location,
      date: eventData.date,
      category: eventData.category,
      poster_id: eventData.posterId,
      latitude: eventData.latitude,
      longitude: eventData.longitude,
      max_applicants: eventData.maxApplicants,
      preferred_gender: eventData.preferredDemographics?.gender,
      min_age: eventData.preferredDemographics?.minAge,
      max_age: eventData.preferredDemographics?.maxAge,
      preferred_interests: eventData.preferredDemographics?.interests,
      is_featured: eventData.isFeatured ?? false,
      featured_until: eventData.featuredUntil,
      status: eventData.status || 'published',
      visibility: eventData.visibility || 'public',
      contact_method: eventData.contactMethod ?? 'in_app',
      source_type: eventData.sourceType || 'user_created',
      system_host: eventData.systemHost ?? false,
      is_bot_created: eventData.isBotCreated ?? false,
      auto_accept_enabled: eventData.autoAcceptEnabled ?? false,
    };

    // Handle custom image URL (for admin community vybes)
    if (eventData.imageUrl) {
      supabaseEventData.image_url = eventData.imageUrl;
      supabaseEventData.image_generation_status = 'completed';
    } else {
      supabaseEventData.image_generation_status = 'pending';
    }

    if (eventData.contactValue !== undefined && eventData.contactValue !== null) {
      supabaseEventData.contact_value = eventData.contactValue;
    }

    const { data, error } = await supabase
      .from('events')
      .insert(supabaseEventData)
      .select()
      .single();

    if (error || !data) throw new Error(error?.message || 'Failed to create event');

    const newEvent = mapEventRow(data);

    // Only trigger image generation if no custom image provided
    if (!eventData.imageUrl) {
      triggerImageGeneration(newEvent.id, eventData.title, eventData.description, eventData.category, eventData.location).catch(err => {
        console.error('Failed to trigger image generation:', err);
      });
    }

    return newEvent;
  } catch (e) {
    console.error('Error in createEvent:', e);
    throw e;
  }
};

async function triggerImageGeneration(
  eventId: string,
  title: string,
  description?: string,
  category?: string,
  location?: string
): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    console.error('Supabase config missing for image generation');
    return;
  }

  const functionUrl = `${supabaseUrl}/functions/v1/generate-vybe-image`;

  try {
    await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        eventId,
        title,
        description,
        category,
        location,
      }),
    });
  } catch (error) {
    console.error('Error calling image generation function:', error);
    throw error;
  }
}

export const setEventAsFeatured = async (eventId: string, duration: number): Promise<boolean> => {
  try {
    let productId: string;
    if (duration <= 3) productId = 'featured3Days';
    else if (duration <= 7) productId = 'featured7Days';
    else productId = 'featured14Days';

    const product = featuredEventProducts[productId];
    if (!product) throw new Error('Invalid duration');

    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session?.access_token) throw new Error('User not authenticated');

    const successUrl = `${window.location.origin}/myvybes?featured_success=true&event_id=${eventId}`;
    const cancelUrl = `${window.location.origin}/myvybes?featured_cancelled=true`;

    // createCheckoutSession should exist in your project (import it where appropriate)
    // const { url } = await createCheckoutSession(
    //   product.priceId,
    //   'payment',
    //   successUrl,
    //   cancelUrl,
    //   session.access_token,
    //   { type: 'featured_event', event_id: eventId, duration: duration.toString() }
    // );
    // if (url) {
    //   window.location.href = url;
    //   return true;
    // }
    // return false;

    console.warn('createCheckoutSession not wired here; leaving as-is.');
    return false;
  } catch (e) {
    console.error('Error in setEventAsFeatured:', e);
    return false;
  }
};

// ---------------------------
// Discovery filters
// ---------------------------
export const findEventsByLocation = async (
  latitude: number,
  longitude: number,
  radius: number,
  _userId?: string
): Promise<Event[]> => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('status', 'published');

    if (error || !data) return [];

    const filtered = data.filter((event: any) => {
      if (!event.latitude || !event.longitude) return false;
      const R = 3958.8; // miles
      const dLat = (event.latitude - latitude) * Math.PI / 180;
      const dLon = (event.longitude - longitude) * Math.PI / 180;
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(latitude * Math.PI / 180) * Math.cos(event.latitude * Math.PI / 180) *
        Math.sin(dLon / 2) ** 2;
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;
      return distance <= radius;
    });

    return filtered.map(mapEventRow);
  } catch (e) {
    console.error('Error in findEventsByLocation:', e);
    return [];
  }
};

export const findEventsByInterests = async (interests: string[], _userId?: string): Promise<Event[]> => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('status', 'published');

    if (error || !data) return [];

    const filtered = data.filter((event: any) => {
      if (!event.preferred_interests || event.preferred_interests.length === 0) return false;
      return interests.some((interest) => event.preferred_interests.includes(interest));
    });

    return filtered.map(mapEventRow);
  } catch (e) {
    console.error('Error in findEventsByInterests:', e);
    return [];
  }
};

// ---------------------------
// Applications
// ---------------------------
export const getApplicationsByUser = async (userId: string): Promise<Application[]> => {
  try {
    const { data, error } = await supabase
      .from('event_applications')
      .select('*')
      .eq('applicant_id', userId)
      .order('created_at', { ascending: false });

    if (error || !data) return [];
    return data.map(mapApplicationRow);
  } catch (e) {
    console.error('Error in getApplicationsByUser:', e);
    return [];
  }
};

export const getUserApplicationForEvent = async (eventId: string, userId: string): Promise<Application | null> => {
  try {
    const { data, error } = await supabase
      .from('event_applications')
      .select('*')
      .eq('event_id', eventId)
      .eq('applicant_id', userId)
      .maybeSingle();

    if (error || !data) return null;
    return mapApplicationRow(data);
  } catch (e) {
    console.error('Error in getUserApplicationForEvent:', e);
    return null;
  }
};

export const getApplicationsForEvent = async (eventId: string): Promise<ApplicationEnriched[]> => {
  try {
    const { data, error } = await supabase
      .from('event_applications')
      .select(`
        id, event_id, applicant_id, message, status, created_at,
        profiles:profiles!event_applications_applicant_id_fkey (
          id, username, full_name, bio, is_verified, rating, rating_count, avatar_url
        )
      `)
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });

    if (error || !data) return [];

    return data.map((row: any) => ({
      id: row.id,
      eventId: row.event_id,
      applicantId: row.applicant_id,
      message: row.message,
      status: row.status,
      createdAt: row.created_at,
      applicant: row.profiles
        ? {
            id: row.profiles.id,
            username: row.profiles.username,
            fullName: row.profiles.full_name,
            bio: row.profiles.bio,
            isVerified: !!row.profiles.is_verified,
            rating: row.profiles.rating ?? null,
            ratingCount: row.profiles.rating_count ?? null,
            avatarUrl: row.profiles.avatar_url ?? null,
          }
        : undefined,
    }));
  } catch (e) {
    console.error('Error in getApplicationsForEvent:', e);
    return [];
  }
};

export const getApplicationById = async (applicationId: string): Promise<Application | undefined> => {
  try {
    const { data, error } = await supabase
      .from('event_applications')
      .select('*')
      .eq('id', applicationId)
      .maybeSingle();

    if (error || !data) return undefined;
    return mapApplicationRow(data);
  } catch (e) {
    console.error('Error in getApplicationById:', e);
    return undefined;
  }
};

export const updateApplicationStatus = async (
  applicationId: string,
  status: 'pending' | 'accepted' | 'rejected' | 'withdrawn' | 'waitlisted'
): Promise<boolean> => {
  try {
    // Get application details before updating
    const { data: application, error: fetchError } = await supabase
      .from('event_applications')
      .select('event_id, applicant_id')
      .eq('id', applicationId)
      .single();

    if (fetchError || !application) {
      console.error('Error fetching application:', fetchError);
      return false;
    }

    const { error } = await supabase
      .from('event_applications')
      .update({ status })
      .eq('id', applicationId);

    if (error) {
      console.error('Error updating application status:', error);
      return false;
    }

    // If status is accepted, trigger the edge function to create vybe thread
    if (status === 'accepted') {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/on-application-accepted`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              eventId: application.event_id,
              applicantId: application.applicant_id,
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error calling on-application-accepted function:', errorText);
        } else {
          console.log('Successfully created vybe thread for accepted application');
        }
      } catch (funcError) {
        console.error('Error calling edge function:', funcError);
        // Don't fail the whole operation if the edge function fails
      }
    }

    return true;
  } catch (e) {
    console.error('Error in updateApplicationStatus:', e);
    return false;
  }
};

/**
 * Apply to an event with duplicate-safe logic:
 * - First attempt to insert (includes host_id).
 * - If duplicate (23505), fetch the existing row:
 *   - If status is 'withdrawn' or 'rejected', re-open to 'pending' (and ensure host_id).
 *   - Otherwise, throw a friendly "already applied" error.
 */
export const applyToEvent = async (eventId: string, userId: string, message: string): Promise<Application> => {
  try {
    // 1) Fetch event to get host/owner id (poster_id)
    const { data: event, error: eventError } = await supabase
      .from('events')
      .select('id, poster_id')
      .eq('id', eventId)
      .single();

    if (eventError || !event) {
      console.error('Error fetching event:', eventError);
      throw new Error('Event not found');
    }

    const hostId = event.poster_id;
    if (!hostId) throw new Error('This Vybe has no host assigned.');

    // 2) Try insert (RLS must allow applicant insert)
    const { data: created, error: createError } = await supabase
      .from('event_applications')
      .insert({
        event_id: eventId,
        applicant_id: userId,
        host_id: hostId,
        message,
        status: 'pending',
      })
      .select()
      .single();

    if (!createError && created) return mapApplicationRow(created);

    // 3) Handle duplicate via unique index (event_id, applicant_id)
    const dup =
      (createError as any)?.code === '23505' ||
      /duplicate key value/i.test((createError as any)?.message || '');
    if (!dup) {
      console.error('Error creating application:', createError);
      throw new Error((createError as any)?.message || 'Failed to apply to event');
    }

    // 4) On duplicate: fetch existing & decide
    const { data: existing, error: fetchErr } = await supabase
      .from('event_applications')
      .select('*')
      .eq('event_id', eventId)
      .eq('applicant_id', userId)
      .single();

    if (fetchErr || !existing) {
      throw new Error('You have already applied to this event.');
    }

    if (['pending', 'accepted', 'waitlisted'].includes(existing.status)) {
      throw new Error('You have an active application for this event');
    }

    // Re-open withdrawn/rejected to pending (and ensure host_id is set)
    const { data: updated, error: updateErr } = await supabase
      .from('event_applications')
      .update({
        message,
        status: 'pending',
        host_id: existing.host_id ?? hostId,
      })
      .eq('id', existing.id)
      .select()
      .single();

    if (updateErr || !updated) {
      console.error('Error updating application:', updateErr);
      throw new Error('Failed to update your application');
    }

    return mapApplicationRow(updated);
  } catch (e: any) {
    console.error('Error in applyToEvent:', e);
    throw e;
  }
};

// Get past vybes (cancelled/expired events for social proof)
export const getPastVybes = async (limit: number = 15): Promise<Event[]> => {
  try {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('events')
      .select(`
        *,
        poster:profiles!events_poster_id_fkey(username, rating, rating_count, subscription_tier, avatar_url)
      `)
      .or(`status.eq.cancelled,status.eq.expired,date.lt.${now}`)
      .eq('visibility', 'public')
      .order('date', { ascending: false })
      .limit(limit);

    if (error || !data) {
      console.error('Error fetching past vybes:', error);
      return [];
    }

    return data.map(mapEventRow);
  } catch (e) {
    console.error('Error in getPastVybes:', e);
    return [];
  }
};

